/**
 * Initialization Loading - User-Friendly Initialization Screen
 *
 * Polished, user-friendly loading experience that hides technical details
 * while providing smooth animations and professional aesthetics.
 * Progress is calculated based on actual service count from YAML config.
 */

import React from "react";

import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";

import { EventBus } from "../../../platform/events/EventBus";
import { useStateMachineContext } from "../../../platform/state/StateMachineProvider";
import debugLogger from "../../../platform/utils/debugLogger";
import { messageVariants } from "../components/animationVariants";
import { InitializationErrorComponent } from "../components/InitializationError";
import { LoadingBackground } from "../components/LoadingBackground";
import { LoadingBranding } from "../components/LoadingBranding";
import { LoadingCompletionBanner } from "../components/LoadingCompletionBanner";
import { LoadingProgressBar } from "../components/LoadingProgressBar";
import { useServiceInitialization } from "../hooks/useServiceInitialization";

interface InitializationLoadingProps {
  eventBus?: EventBus;
}

export const InitializationLoading: React.FC<InitializationLoadingProps> = ({
  eventBus,
}) => {
  const theme = useTheme();

  // Try to get state machine context, but make it optional for early initialization
  let stateMachine: ReturnType<typeof useStateMachineContext> | null = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- intentionally conditional; component renders both inside and outside StateMachineProvider
    stateMachine = useStateMachineContext();
  } catch {
    // StateMachineProvider not available yet (e.g., during platform config initialization)
    // This is expected and handled gracefully
    debugLogger.debug(
      "StateMachineProvider not available yet, using standalone initialization mode",
    );
  }

  const {
    overallProgress,
    currentMessage,
    initializationErrors,
    hasErrors,
    isGateBlocked,
    isInitializing,
  } = useServiceInitialization({ eventBus, stateMachine });

  // Show error component if there are ANY errors
  if (hasErrors && initializationErrors.length > 0) {
    return (
      <InitializationErrorComponent
        errors={initializationErrors}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        position: "relative",
        bgcolor: "background.default",
        overflow: "hidden",
        // Subtle gradient background
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
      }}
    >
      {/* Animated background elements */}
      <LoadingBackground />

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: 500,
          width: "100%",
          padding: "0 24px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Logo/Branding */}
        <LoadingBranding />

        {/* Loading Spinner */}
        <Box sx={{ mb: 4 }}>
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: theme.palette.primary.main,
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
        </Box>

        {/* User-Friendly Message */}
        <motion.div
          key={currentMessage}
          variants={messageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.3 }}
          style={{
            marginBottom: 32,
            minHeight: 32,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 400,
              letterSpacing: "0.01em",
            }}
          >
            {currentMessage}
          </Typography>
        </motion.div>

        {/* Progress Bar */}
        <LoadingProgressBar progress={overallProgress} />

        {/* Completion State - Show when initialization is complete */}
        <LoadingCompletionBanner
          visible={!isInitializing && overallProgress >= 100}
          isGateBlocked={isGateBlocked}
        />
      </motion.div>
    </Box>
  );
};

export default InitializationLoading;
